import Pubsub, { PubsubCallback } from '../pubsub/pubsub'
import MessengerIframe from './messenger.iframe'
import {
  DONATION_CHANGED_MSG_FROM_APP,
  DONATION_CLOSE_CONFIRMED_MSG_FROM_APP,
  DONATION_CLOSE_INTERCEPT_READY_MSG_FROM_APP,
  DONATION_COMPLETED_MSG_FROM_APP,
  DONATION_READY_MSG_FROM_APP,
  ANALYTICS_EVENT_MSG_FROM_APP,
  GA4_INIT_MSG_FROM_APP,
  MsgType,
  TEALIUM_ANALYTICS_EVENT_MSG_FROM_APP,
} from './messenger.messages'

interface MessengerProps {
  win: Window
}

/**
 * Messenger class responsible for handling postMessage events between the app and the iframe,
 * and initializing the MessengerIframe class to handle queued messages.
 */
export default class Messenger {
  private props: MessengerProps
  private pubsub: Pubsub<MsgType>
  public iframe: MessengerIframe
  private messageListener: ((event: MessageEvent) => void) | null = null
  private destroyed = false

  constructor(props: MessengerProps) {
    this.props = props
    this.pubsub = new Pubsub()
    this.iframe = new MessengerIframe()
    this._subscribe()
  }

  private _subscribe = () => {
    this.messageListener = (event: MessageEvent) => {
      const trustedOrigins = [
        process.env.EMBEDDED_APP_URI,
        process.env.EMBEDDED_APP_URI_GFM,
        // Legacy standalone embedded-app origin. Hardcoded and kept trusted during
        // the monolith migration so SDKs still served from it aren't rejected.
        // Remove once the migration is stable: https://gofundme.atlassian.net/browse/PRO-6540
        'https://giving.classy.org/embedded',
      ].filter((origin): origin is string => Boolean(origin))

      const isAllowedOrigin =
        event.origin === this.props.win.location.origin ||
        trustedOrigins.some((origin) => origin.startsWith(event.origin))

      if (!isAllowedOrigin) {
        console.warn('Untrusted origin:', event.origin)
        return
      }

      if (!event.data) {
        return
      }

      const { type, payload } = event.data

      if (type && payload) {
        this.pubsub.publish({ name: type, data: { type, payload, source: event.source } })
      }
    }

    this.props.win.addEventListener('message', this.messageListener)
  }

  destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    if (this.messageListener) {
      this.props.win.removeEventListener('message', this.messageListener)
      this.messageListener = null
    }

    this.pubsub.unsubscribeAll()
    this.iframe.reset()
  }

  /**
   * Subscribes to GA4 initialization messages.
   *
   * @param callback - The function to handle GA4 init messages
   */
  public onGA4Init = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(GA4_INIT_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to analytics event messages.
   *
   * @param callback - The function to handle analytics event messages
   */
  public onAnalyticsEvent = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(ANALYTICS_EVENT_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to Tealium analytics event messages.
   *
   * @param callback - The function to handle Tealium analytics event messages
   */
  public onTealiumAnalyticsEvent = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(TEALIUM_ANALYTICS_EVENT_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to donation ready messages and passes the callback to MessengerIframe.
   *
   * @param callback - The function to handle donation ready messages
   */
  public onDonationReady = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(DONATION_READY_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to donation changed messages.
   *
   * @param callback - The function to handle donation changed messages
   */
  public onDonationChanged = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(DONATION_CHANGED_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to donation completed messages.
   *
   * @param callback - The function to handle donation completed messages
   */
  public onDonationCompleted = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(DONATION_COMPLETED_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to messages declaring the embedded app ready to intercept close requests.
   *
   * @param callback - The function to handle close intercept ready messages
   */
  public onDonationCloseInterceptReady = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(DONATION_CLOSE_INTERCEPT_READY_MSG_FROM_APP, callback)
  }

  /**
   * Subscribes to messages confirming the embedded app finished its close flow.
   *
   * @param callback - The function to handle close confirmed messages
   */
  public onDonationCloseConfirmed = (callback: PubsubCallback<MsgType>): void => {
    this.pubsub.subscribe(DONATION_CLOSE_CONFIRMED_MSG_FROM_APP, callback)
  }
}
